import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { AttorneysNeedingReviewsGetResponse } from "../../../api/src/users/route";
import type {
  AttorneyReviewCreateRequest,
  AttorneyReviewsPostResponse,
} from "../../../api/src/users/attorney/attorney-reviews/route";
import config from "../config";
import { prepareHeaders } from "./utils";
import type {
  AttorneyReviewsAdminGetResponse,
  AttorneyReviewsAdminPatchResponse,
} from "../../../api/src/admin/attorney-reviews/route";
import type { AttorneyReviewStatus } from "../../../api/src/types";

export const attorneyReviewsApi = createApi({
  reducerPath: "attorneyReviewsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: ["AttorneysNeedingReviews", "AttorneyReviewsAdmin"],
  endpoints: (builder) => ({
    // Get attorneys that need reviews
    getAttorneysNeedingReviews: builder.query<
      AttorneysNeedingReviewsGetResponse,
      string
    >({
      query: (userId) => `/users/${userId}/attorneys-needing-reviews`,
      providesTags: ["AttorneysNeedingReviews"],
    }),

    // Submit attorney review
    createAttorneyReview: builder.mutation<
      AttorneyReviewsPostResponse,
      { userId: string; attorneyId: string; data: AttorneyReviewCreateRequest }
    >({
      query: ({ userId, attorneyId, data }) => ({
        url: `/users/${userId}/attorneys/${attorneyId}/attorney-reviews`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["AttorneysNeedingReviews"],
    }),
    // Admin get attorney reviews
    getAttorneyReviewsAdmin: builder.query<
      AttorneyReviewsAdminGetResponse,
      { attorneyId?: string }
    >({
      query: ({ attorneyId }) =>
        `/admin/attorney-reviews${attorneyId ? `?attorneyId=${attorneyId}` : ""}`,
      providesTags: ["AttorneyReviewsAdmin"],
    }),
    // Admin update attorney review status
    updateAttorneyReviewStatus: builder.mutation<
      AttorneyReviewsAdminPatchResponse,
      { reviewId: string; status: AttorneyReviewStatus }
    >({
      query: ({ reviewId, status }) => ({
        url: `/admin/attorney-reviews/${reviewId}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["AttorneyReviewsAdmin"],
    }),
  }),
});

export const {
  useGetAttorneysNeedingReviewsQuery,
  useCreateAttorneyReviewMutation,
  useGetAttorneyReviewsAdminQuery,
  useUpdateAttorneyReviewStatusMutation,
} = attorneyReviewsApi;
